import React, { useContext } from 'react';
import { useNavigate } from 'react-router';
import './MaturityInstruction.scss';
import { TransferDetails } from "./TransferDetails/TransferDetails";
import { customerDataContext } from "../../../contexts/CustomerDataProvider";
import checkIcon from "../../../assets/images/check-navy-circle.svg";
import {
    addDays,
    formatCurrency,
    formatDate,
    formatDestinationAccountType,
    formatProductTerm
} from "../../../utils/helpers";
import Button from "../../Form/Button/Button";
import TextInput from "../../Form/TextInput/TextInput";
import {InstructionCard} from "./InstructionCard/InstructionCard";
import {useUnifiedAnalytics} from "../../../contexts/AnalyticsWrapper";
import { productTypes } from "../../../utils/productHelpers";
import MaturityNotice, { maturityNoticeVariants } from "../../MaturityNotice";
import { fisaMaturityLearnMoreUrl, fisaMaturityPageSources } from "../../MaturityNotice/MaturityNoticeConfig";
import withFeatureFlags from "../../FeatureFlags/withFeatureFlags";

/** @import {FrsProductData} from '../MyAccountTypes' */
/**@import {Account} from '../../../contexts/CustomerDataProviderTypes' */
const MaturityInstructionComponent = (/** @type {{profileData: FrsProductData; featureEnabled: (flag: string) => boolean}} */ props) => {
    const navigate = useNavigate();
    const data = useContext(customerDataContext)?.data;
    const destinationAccountId = props.profileData?.maturingAccount?.maturity?.destinationAccountId || '';

    /** @returns {Account | null} */
    function getDestinationAccount(/** @type {string}} */ id) {
        const accounts = data?.customer.accounts.filter(a => a.id === id);
        return accounts && accounts[0] || null;
    }

    const ins = props.profileData.instruction;
    const productNtbInterestRate = Number(props.profileData.rolloverAccountProduct?.rate);
    const displayNtbRate = props.profileData.rollOverInterestRate < productNtbInterestRate;
    //const rate = displayNtbRate ? productNtbInterestRate : props.profileData.rollOverInterestRate;
    const rate = props.profileData.rollOverInterestRate;
    const term = formatProductTerm(props.profileData.rolloverAccountProduct?.productTerm);

    const instructionData = [
        { label: "Fixed term", value: term },
        { label: "Renewal rate", value: `${rate.toFixed(2)}% AER*` },
        { label: "Renewal amount", value: ins === "RolloverPartialFundPayoutInterest" ?
                        formatCurrency(props.profileData.maturingAccount?.maturity?.partialRolloverAmount) :
                        ins === "RolloverAllFunds" ?
                        formatCurrency(props.profileData.maturingAccount.maturity?.calculatedBalanceAtMaturity) :
                        formatCurrency(props.profileData.maturingAccount.balance)
        }
    ];
    
    const transferData = [
        ...(ins === "RolloverPartialFundPayoutInterest" ? [{
            label: "Transfer amount",
            amount: formatCurrency(props.profileData.maturingAccount.maturity?.remainingBalanceWithdrawalAmount),
            description: `Paid to your ${getDestinationAccount(destinationAccountId)?.product.name ?? 'linked account'} – ${getDestinationAccount(destinationAccountId)?.accountNumber ?? props.profileData.maturingAccount.linkedAccountNumber}`
        }] : []),
        ...(ins === "RolloverPartialFundPayoutInterest" || ins === "RolloverPayawayInterest" ? [{
            label: "Interest amount",
            amount: formatCurrency(props.profileData.maturingAccount.maturity?.calculatedTotalInterest),
            description: `Paid to your linked account – ${props.profileData.maturingAccount.linkedAccountNumber}`
        }] : [])
    ];

    function formatter(/** @type {string | Date | undefined} */ date) {
        if (date) {
            return formatDate(new Date(date));
        }
        return '';
    }

    const analytics = useUnifiedAnalytics();
    const isMaturingState = props.profileData?.maturityWindowStatus === "InstructionOpen";

    const trackMaturityClick = (/** @type {string} */ action, /** @type {string} */ destinationUrl) => {
        analytics.trackAnalyticsClickEvent(
            action,
            destinationUrl,
            "tab",
            props.profileData.maturingAccount.product.code,
            "maturity",
            {},
            "click",
            `account-details|click|${props.profileData.maturingAccount.product.code}-change-my-instruction`,
            "maturity"
        );
    };

    const loadFisaLearnMorePage = () => {
        try {
            trackMaturityClick('learn more', fisaMaturityLearnMoreUrl);
        } finally {
            navigate(fisaMaturityLearnMoreUrl, { state: { source: fisaMaturityPageSources.ManageAccountMaturity } });
        }
    };

    const loadMaturityPage = (/** @type {string} */ action) => {
        const destinationUrl = `/maturity-instruction/${props.profileData.accountId}`;

        trackMaturityClick(action, destinationUrl);
        window.location.pathname = destinationUrl;
    };

    function formatInstruction(/** @type {string | undefined} */ instruction) {
        if (!instruction || instruction === "PayawayLinked" || instruction === "PayawayOtherAccount") {
            return "Close your Fixed Rate Saver";
        }
        if (instruction === "RolloverAllFunds") {
            return "Renew everything";
        }
        if (instruction === "RolloverPayawayInterest") {
            return "Renew original balance only";
        }
        if (instruction === "RolloverPartialFundPayoutInterest") {
            return "Renew some of your balance";
        }
        return "";
    }

    if (!isMaturingState) {
        return null;
    }

    if (props.profileData.maturingAccount?.product?.productType === productTypes.FixedRateCashISA) {
        if (!props.featureEnabled('EnableFisaMaturity')) {
            return null;
        }

        return (
            <MaturityNotice
                account={props.profileData.maturingAccount}
                variant={maturityNoticeVariants.ManageAccount}
                onAction={loadFisaLearnMorePage}
                className="maturity-instruction__notice"
            />
        );
    }

    return (
        <div className={"instructionContainer"}>
            <header className={"instructionHeader"}>
                <h1 className={"instructionTitle"}>Maturity instruction</h1>
            </header>

            <section className={"summarySection"}>
                <div className={"choiceSummary"}>
                    <span className={"summaryLabel"}>You've chosen to: </span>
                    <span className={"summaryValue"}>
                        {formatInstruction(ins)} on {formatter(props.profileData.maturityDate)}
                    </span>
                </div>
            </section>
            
            {!ins || ins === "PayawayLinked" || ins === "PayawayOtherAccount" ? (
                <main className={"mainContent2"}>
                <div className="list">
                    <div className="list__custom-bullet">
                        <img src={checkIcon} alt="Check Icon" />
                        <p>
                            <b className="text__bold text__small text__navy">Close your Fixed Rate Saver</b>
                        </p>
                    </div>

                    <div className="list__custom-bullet">
                        <img src={checkIcon} alt="Check Icon" />
                        <p>
                            <b className="text__bold text__small text__navy">Pay your balance and interest to your {formatDestinationAccountType(props.profileData.payawayDestinationAccountType)} account</b>
                        </p>
                    </div>
                    
                    <div className={"transferAmountGap"}></div>
                    <div className="container__flex-row">
                        <TextInput className="my-account__input"
                                   value={formatCurrency(props.profileData.calculatedBalanceAtMaturity)} label='Amount'
                                   disabled />
                    </div>
                </div>
                </main>
            ) : null}
            
            {ins && ins !== "PayawayLinked" && ins !== "PayawayOtherAccount" && (
                <main className={"mainContent"}>
                <div className={"detailsGrid"}>
                    <div className={"instructionDetails"}>
                        {instructionData.map((item, index) => (
                            <InstructionCard
                                key={index}
                                label={item.label}
                                value={item.value}
                            />
                        ))}
                    </div>

                    <div className={"transferDetails"}>
                        {transferData.map((item, index) => (
                            <TransferDetails
                                key={index}
                                label={item.label}
                                amount={item.amount}
                                description={item.description}
                            />
                        ))}
                    </div>

                    <div className={"instructionInfoMessage"}>
                        <img
                            src="https://cdn.builder.io/api/v1/image/assets/TEMP/f2d20270adf2c37adfa59a5694ac8c8d56e529b00f8b244f64528a5e7c0f913a?placeholderIfAbsent=true&apiKey=bb6df4adb5e94b9594b87e7d47ba4491"
                            alt=""
                            className={"infoIcon"}
                        />
                        <p className={"infoText"}>
                            <span>You'll receive our best available rate on </span>
                            <strong>
                                {props?.profileData?.maturityDate ? new Date(props.profileData.maturityDate).toLocaleDateString('en-US', { weekday: 'long' }) : ''} {formatter(props.profileData.maturityDate)}
                            </strong>
                                <span>, which might be our new customer rate or your renewal rate, depending on which one is higher. Renewal rates will never change, but the customer rates may go up or down.</span>
                        </p>
                    </div>
                </div>

                </main>
            )}

            {props.profileData.maturityWindowStatus === "InstructionOpen" &&
                <footer className={"footerInstruction footer--maturity-instruction"}>
                    {ins && ins !== "PayawayLinked" && ins !== "PayawayOtherAccount" && (
                        <div className={"fundingWindow"}>
                            <span className={"windowLabel"}>Funding window period: </span>
                            <span
                                className={"windowDates"}>{formatter(props.profileData.maturityDate)} - {props.profileData.maturityDate ? formatter(addDays(props.profileData.maturityDate, 7)) : ''}</span>
                        </div>
                    )}
                    <>
                        <Button size='med' customClassNames={["maturity3__padding"]} label={"Change my instruction"}
                                onClick={() => loadMaturityPage('change my instruction')}/>

                        <p className={"disclaimer"}>
                            You can change your instruction up
                            until <b>{props.profileData.maturityWindowCloseTime}</b> on {formatter(props.profileData.maturityWindowCloseDate)}.
                        </p>
                    </>

                </footer>
            }
        </div>
    );
};

export const MaturityInstruction = withFeatureFlags(MaturityInstructionComponent);
